import { Filter, X } from 'lucide-react'

interface FilterChipBarProps {
  chips: { label: string; clear: () => void }[]
  onClearAll: () => void
}

/**
 * Compact inline "applied filters" strip: a funnel icon, red-tinted chips with individual ✕ buttons,
 * and a "Clear all" link. Rendered inline in the Issues/Coverage toolbar row (PanelTabs middle slot),
 * which scrolls horizontally on overflow — so chips stay `flex-none whitespace-nowrap`. Renders
 * nothing when there are no chips.
 */
export function FilterChipBar({ chips, onClearAll }: FilterChipBarProps) {
  if (chips.length === 0) {
    return null
  }
  return (
    <div className="flex w-full min-w-0 items-center gap-2">
      <Filter className="h-4 w-4 flex-none text-[#4f46e5]" />
      {/* Only the chips scroll horizontally — the funnel and "Clear all" stay pinned so they never
          scroll out of reach when many filters are applied. */}
      <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto">
        {chips.map((c) => (
          <span
            key={c.label}
            className="inline-flex flex-none items-center gap-[7px] whitespace-nowrap rounded-lg border border-[#f3c8c8] bg-[#fdecec] px-[10px] py-[4px] text-[11.5px] font-semibold text-[#dc2626]"
          >
            {c.label}
            <button
              type="button"
              onClick={c.clear}
              aria-label={`Remove ${c.label} filter`}
              className="grid h-4 w-4 place-items-center rounded-full bg-[#dc2626]/15 transition-colors hover:bg-[#dc2626]/30 active:brightness-90 focus-visible:ring-2 focus-visible:ring-blue-500/60"
            >
              <X className="h-[11px] w-[11px] stroke-[2.5]" />
            </button>
          </span>
        ))}
      </div>
      <button
        type="button"
        onClick={onClearAll}
        className="flex-none whitespace-nowrap rounded text-[11.5px] font-semibold text-[#4338ca] underline underline-offset-2 hover:text-gis-ink active:opacity-70 focus-visible:ring-2 focus-visible:ring-blue-500/60"
      >
        Clear all
      </button>
    </div>
  )
}
